/**
 * Cart resilience for cookie-blocked browsers (e.g. Edge with cookies disabled).
 *
 * Normal path: verify Ajax adds via /cart.js before showing the drawer.
 * Express path: mirror the bag in memory + window.name, then checkout via URL-encoded permalink.
 *
 * IMPORTANT: Express mode fixes the broken add-to-cart UX and enables same-tab multi-item checkout.
 * Shopify checkout with cookies fully disabled is not 100% guaranteed — validate on production Edge.
 */
(function () {
  window.FoxTheme = window.FoxTheme || {};

  const STORAGE_KEY = 'cart_recovery_items';
  const EPHEMERAL_WINDOW_NAME_PREFIX = 'gerber:express-cart:';
  const EXPRESS_BAR_ID = 'express-checkout-bar';
  const EXPRESS_TOAST_ID = 'express-cart-toast';
  const COOKIE_PROBE = 'gerber_cookie_probe';

  let cookieProbeResult = null;
  let storageProbeResult = null;

  const ephemeralCart = {
    items: [],

    init() {
      this.hydrate();
      if (this.items.length) {
        cartResilience.syncExpressCartUI();
        cartResilience.renderExpressCheckoutBar();
      }
    },

    hydrate() {
      this.items = [];
      try {
        if (window.name && window.name.startsWith(EPHEMERAL_WINDOW_NAME_PREFIX)) {
          const parsed = JSON.parse(window.name.slice(EPHEMERAL_WINDOW_NAME_PREFIX.length));
          if (Array.isArray(parsed)) {
            this.items = cartResilience.normalizeItems(parsed);
          }
        }
      } catch (error) {
        this.items = [];
      }
    },

    persist() {
      try {
        window.name = EPHEMERAL_WINDOW_NAME_PREFIX + JSON.stringify(this.items);
      } catch (error) {
        // window.name may be unavailable in rare embed contexts.
      }
    },

    addItems(items) {
      const normalized = cartResilience.normalizeItems(items);
      normalized.forEach((incoming) => {
        const existing = this.items.find((item) => item.variantId === incoming.variantId);
        if (existing) {
          existing.quantity += incoming.quantity;
          if (incoming.properties && Object.keys(incoming.properties).length) {
            existing.properties = incoming.properties;
          }
        } else {
          this.items.push({ ...incoming });
        }
      });
      this.persist();
      cartResilience.syncExpressCartUI();
      cartResilience.renderExpressCheckoutBar();
      cartResilience.showExpressToast('Added to your bag');
    },

    getItemCount() {
      return this.items.reduce((total, item) => total + item.quantity, 0);
    },

    hasPersonalizedItems() {
      return this.items.some((item) => item.properties && Object.keys(item.properties).length > 0);
    },

    getCheckoutItems() {
      return this.items.filter((item) => !item.properties || !Object.keys(item.properties).length);
    },

    clear() {
      this.items = [];
      this.persist();
      cartResilience.syncExpressCartUI();
      cartResilience.removeExpressCheckoutBar();
    },
  };

  const cartResilience = {
    canUseCookies() {
      if (cookieProbeResult !== null) return cookieProbeResult;

      try {
        document.cookie = `${COOKIE_PROBE}=1; path=/; SameSite=Lax`;
        cookieProbeResult = document.cookie.indexOf(`${COOKIE_PROBE}=1`) !== -1;
        document.cookie = `${COOKIE_PROBE}=; path=/; Max-Age=0; SameSite=Lax`;
        return cookieProbeResult;
      } catch (error) {
        cookieProbeResult = false;
        return false;
      }
    },

    canUseSessionStorage() {
      if (storageProbeResult !== null) return storageProbeResult;

      if (FoxTheme.config && FoxTheme.config.hasLocalStorage === false) {
        storageProbeResult = false;
        return false;
      }

      try {
        const key = 'cart-resilience:test';
        window.sessionStorage.setItem(key, '1');
        window.sessionStorage.removeItem(key);
        storageProbeResult = true;
        return true;
      } catch (error) {
        storageProbeResult = false;
        return false;
      }
    },

    isClientStorageAvailable() {
      return this.canUseCookies() && this.canUseSessionStorage();
    },

    isExpressMode() {
      return !this.isClientStorageAvailable();
    },

    getStoreOrigin() {
      return window.shopUrl || window.location.origin;
    },

    getCartUrl() {
      return (FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
    },

    getCartAddUrl() {
      const url = (FoxTheme.routes && FoxTheme.routes.cart_add_url) || '/cart/add';
      return url.endsWith('.js') ? url : `${url}.js`;
    },

    getCartJsFetchOptions() {
      return {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      };
    },

    getCartAddFetchOptions(body) {
      return {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      };
    },

    async fetchCart() {
      const response = await fetch(`${this.getCartUrl()}.js`, this.getCartJsFetchOptions());
      if (!response.ok) {
        throw new Error(`Failed to fetch cart (${response.status})`);
      }
      return response.json();
    },

    normalizeItems(items) {
      if (!items || !items.length) return [];
      return items.map((item) => ({
        variantId: String(item.variantId ?? item.id),
        quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
        properties: item.properties || null,
      }));
    },

    cartContainsVariant(cart, variantId, minQuantity = 1) {
      if (!cart || !Array.isArray(cart.items)) return false;
      const id = String(variantId);
      const line = cart.items.find((item) => String(item.variant_id) === id);
      if (!line) return false;
      return line.quantity >= minQuantity;
    },

    async verifyCartAdd(items) {
      const normalized = this.normalizeItems(items);
      if (!normalized.length) {
        return { verified: false, cart: null };
      }

      try {
        const cart = await this.fetchCart();
        const verified = normalized.every((item) =>
          this.cartContainsVariant(cart, item.variantId, item.quantity)
        );
        return { verified, cart };
      } catch (error) {
        console.warn('[cart-resilience] Cart verification failed:', error);
        return { verified: false, cart: null };
      }
    },

    buildPermalink(items, { cartPage = false } = {}) {
      const normalized = this.normalizeItems(items);
      if (!normalized.length) return `${this.getCartUrl()}`;

      const segment = normalized.map((item) => `${item.variantId}:${item.quantity}`).join(',');
      const root = (FoxTheme.routes && FoxTheme.routes.root_url) || '/';
      const path = root.endsWith('/') ? `${root}cart/${segment}` : `${root}/cart/${segment}`;
      const url = new URL(path, this.getStoreOrigin()).href;
      return cartPage ? `${url}?storefront=true` : url;
    },

    buildExpressCheckoutUrl() {
      const checkoutItems = ephemeralCart.getCheckoutItems();
      if (!checkoutItems.length) return null;
      return this.buildPermalink(checkoutItems, { cartPage: false });
    },

    buildExpressCartPageUrl() {
      const checkoutItems = ephemeralCart.getCheckoutItems();
      if (!checkoutItems.length) return null;
      return this.buildPermalink(checkoutItems, { cartPage: true });
    },

    syncExpressCartUI() {
      const count = ephemeralCart.getItemCount();
      document.documentElement.classList.toggle('cart-has-items', count > 0);
      document.documentElement.classList.toggle('express-cart-active', count > 0);
      document.documentElement.dataset.expressCartCount = String(count);

      if (FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: {
            item_count: count,
            items: [],
            express: true,
          },
        });
      }
    },

    showExpressToast(message) {
      let toast = document.getElementById(EXPRESS_TOAST_ID);
      if (!toast) {
        toast = document.createElement('div');
        toast.id = EXPRESS_TOAST_ID;
        toast.setAttribute('role', 'status');
        toast.style.cssText =
          'position:fixed;left:50%;top:1.25rem;transform:translateX(-50%);z-index:10000;padding:0.7rem 1rem;border-radius:999px;background:#111;color:#fff;font-size:0.9rem;box-shadow:0 8px 24px rgba(0,0,0,.2);';
        document.body.appendChild(toast);
      }

      toast.textContent = message;
      toast.hidden = false;
      clearTimeout(toast._hideTimer);
      toast._hideTimer = setTimeout(() => {
        toast.hidden = true;
      }, 2200);
    },

    removeExpressCheckoutBar() {
      const bar = document.getElementById(EXPRESS_BAR_ID);
      if (bar) bar.remove();
      document.body.style.paddingBottom = '';
    },

    renderExpressCheckoutBar(options = {}) {
      const { checkoutBlocked = false } = options;
      const count = ephemeralCart.getItemCount();
      if (!count) {
        this.removeExpressCheckoutBar();
        return;
      }

      let bar = document.getElementById(EXPRESS_BAR_ID);
      if (!bar) {
        bar = document.createElement('div');
        bar.id = EXPRESS_BAR_ID;
        bar.setAttribute('role', 'region');
        bar.setAttribute('aria-label', 'Express checkout');
        bar.style.cssText =
          'position:fixed;left:0;right:0;bottom:0;z-index:9998;padding:0.85rem 1rem;background:#111;color:#fff;box-shadow:0 -4px 24px rgba(0,0,0,.18);';
        document.body.appendChild(bar);
      }

      const checkoutUrl = this.buildExpressCheckoutUrl();
      const cartPageUrl = this.buildExpressCartPageUrl();
      const hasPersonalized = ephemeralCart.hasPersonalizedItems();
      const checkoutDisabled = !checkoutUrl;
      const itemLabel = count === 1 ? '1 item' : `${count} items`;

      bar.innerHTML = `
        <div style="max-width:72rem;margin:0 auto;display:flex;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:wrap;">
          <div style="min-width:12rem;">
            <strong style="display:block;font-size:0.95rem;">${itemLabel} ready</strong>
            <span style="display:block;font-size:0.8rem;opacity:0.85;margin-top:0.15rem;">
              ${
                checkoutBlocked
                  ? 'Checkout could not start because this browser blocks cookies. Turn cookies back on in Edge, or switch browsers.'
                  : 'Express checkout mode is active because this browser blocks cookies.'
              }
            </span>
            ${
              hasPersonalized
                ? '<span style="display:block;font-size:0.8rem;color:#f6c177;margin-top:0.35rem;">Personalized items still need cookies. Standard items can continue below.</span>'
                : ''
            }
          </div>
          <div style="display:flex;gap:0.65rem;align-items:center;margin-left:auto;flex-wrap:wrap;">
            <button type="button" data-express-clear style="background:transparent;border:1px solid rgba(255,255,255,.35);color:#fff;border-radius:999px;padding:0.55rem 0.9rem;font:inherit;cursor:pointer;">
              Clear
            </button>
            ${
              cartPageUrl
                ? '<button type="button" data-express-view-cart style="background:transparent;border:1px solid rgba(255,255,255,.35);color:#fff;border-radius:999px;padding:0.55rem 0.9rem;font:inherit;cursor:pointer;">View bag</button>'
                : ''
            }
            <button type="button" data-express-checkout ${checkoutDisabled ? 'disabled' : ''} style="background:#fff;border:0;color:#111;border-radius:999px;padding:0.65rem 1.15rem;font:inherit;font-weight:600;cursor:pointer;${checkoutDisabled ? 'opacity:.45;cursor:not-allowed;' : ''}">
              Continue to secure checkout
            </button>
          </div>
        </div>
      `;

      const checkoutButton = bar.querySelector('[data-express-checkout]');
      const clearButton = bar.querySelector('[data-express-clear]');
      const viewCartButton = bar.querySelector('[data-express-view-cart]');

      if (checkoutButton && checkoutUrl) {
        checkoutButton.onclick = () => {
          window.location.assign(checkoutUrl);
        };
      }

      if (viewCartButton && cartPageUrl) {
        viewCartButton.onclick = () => {
          window.location.assign(cartPageUrl);
        };
      }

      if (clearButton) {
        clearButton.onclick = () => {
          ephemeralCart.clear();
        };
      }

      document.body.style.paddingBottom = `${bar.offsetHeight}px`;
    },

    bindExpressHeaderCart() {
      document.addEventListener(
        'click',
        (event) => {
          if (!this.isExpressMode() || ephemeralCart.getItemCount() === 0) return;

          const cartTrigger = event.target.closest('.cart-drawer-button, a[href*="/cart"]');
          if (!cartTrigger) return;

          event.preventDefault();
          event.stopPropagation();

          const bar = document.getElementById(EXPRESS_BAR_ID);
          if (bar) {
            bar.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else {
            this.renderExpressCheckoutBar();
          }
        },
        true
      );
    },

    detectCheckoutBlockedOnLoad() {
      const params = new URLSearchParams(window.location.search);
      const blocked =
        params.has('no_cookies_from_redirect') ||
        window.location.href.indexOf('no_cookies_from_redirect') !== -1;

      if (blocked && this.isExpressMode()) {
        this.renderExpressCheckoutBar({ checkoutBlocked: true });
      }
    },

    stashRecoveryItems(items) {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            items: this.normalizeItems(items),
            ts: Date.now(),
          })
        );
      } catch (error) {
        // sessionStorage may also be restricted.
      }
    },

    clearRecoveryItems() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // Ignore storage errors.
      }
    },

    redirectToPermalink(items) {
      this.stashRecoveryItems(items);
      window.location.assign(this.buildPermalink(items));
    },

    formHasLineItemProperties(form) {
      if (!form) return false;
      return !!form.querySelector('[name^="properties["]');
    },

    injectPropertiesIntoForm(form, properties) {
      if (!form || !properties) return;

      Object.entries(properties).forEach(([key, value]) => {
        const name = `properties[${key}]`;
        let input = Array.from(form.elements).find((element) => element.name === name);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value == null ? '' : String(value);
      });
    },

    submitNativeForm(form) {
      if (!form) return false;
      form.setAttribute('data-cart-resilience-fallback', 'true');
      HTMLFormElement.prototype.submit.call(form);
      return true;
    },

    handleExpressModeAdd(items, options = {}) {
      const normalized = this.normalizeItems(items);
      const { form = null, injectProperties = false } = options;
      const hasProperties = normalized.some((item) => item.properties && Object.keys(item.properties).length > 0);

      if (hasProperties && form) {
        if (injectProperties && normalized[0] && normalized[0].properties) {
          this.injectPropertiesIntoForm(form, normalized[0].properties);
        }
        this.submitNativeForm(form);
        return { recovered: true, verified: false, method: 'form' };
      }

      ephemeralCart.addItems(normalized);
      return { recovered: true, verified: false, ephemeral: true, method: 'express' };
    },

    async ensureCartPersisted(items, options = {}) {
      const normalized = this.normalizeItems(items);
      const { form = null, silentRedirect = true, injectProperties = false, forceFormPost = false } = options;

      if (this.isExpressMode()) {
        return this.handleExpressModeAdd(normalized, { form, injectProperties });
      }

      const { verified, cart } = await this.verifyCartAdd(normalized);
      if (verified) {
        this.clearRecoveryItems();
        return { recovered: false, verified: true, cart };
      }

      this.stashRecoveryItems(normalized);

      const needsFormPost =
        forceFormPost ||
        injectProperties ||
        this.formHasLineItemProperties(form) ||
        normalized.some((item) => item.properties && Object.keys(item.properties).length > 0);

      if (needsFormPost && form) {
        if (injectProperties && normalized[0] && normalized[0].properties) {
          this.injectPropertiesIntoForm(form, normalized[0].properties);
        }
        this.submitNativeForm(form);
        return { recovered: true, verified: false, cart: null, method: 'form' };
      }

      if (silentRedirect !== false) {
        this.redirectToPermalink(normalized);
        return { recovered: true, verified: false, cart: null, method: 'permalink' };
      }

      return { recovered: false, verified: false, cart: null };
    },

    async bundleCartSections(cartJson) {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      if (!sectionsToBundle.length) {
        return cartJson;
      }

      const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionsToBundle.join(',')}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (sectionsResponse.ok) {
        cartJson.sections = await sectionsResponse.json();
      }

      return cartJson;
    },

    async completeAddAndShowCart({ items, form = null, parsedState = null, injectProperties = false, onSuccess }) {
      if (this.isExpressMode()) {
        const expressResult = this.handleExpressModeAdd(items, { form, injectProperties });
        return {
          recovered: true,
          ephemeral: expressResult.ephemeral,
          method: expressResult.method,
        };
      }

      const persistence = await this.ensureCartPersisted(items, {
        form,
        silentRedirect: true,
        injectProperties,
        forceFormPost: injectProperties,
      });

      if (persistence.recovered || persistence.ephemeral) {
        return {
          recovered: true,
          ephemeral: persistence.ephemeral,
          method: persistence.method,
        };
      }

      let cartJson = persistence.cart;
      if (!cartJson) {
        cartJson = await this.fetchCart();
      }

      if (parsedState && parsedState.sections) {
        cartJson.sections = parsedState.sections;
      } else {
        cartJson = await this.bundleCartSections(cartJson);
      }

      if (typeof onSuccess === 'function') {
        await onSuccess(cartJson, parsedState);
      }

      return { recovered: false, cartJson };
    },
  };

  FoxTheme.cartResilience = cartResilience;
  FoxTheme.ephemeralCart = ephemeralCart;

  const bootExpressCart = () => {
    if (!cartResilience.isExpressMode()) return;

    document.documentElement.classList.add('express-cart-mode');
    ephemeralCart.init();
    cartResilience.bindExpressHeaderCart();
    cartResilience.detectCheckoutBlockedOnLoad();
  };

  const bootWhenReady = () => {
    bootExpressCart();
    window.setTimeout(bootExpressCart, 0);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootWhenReady);
  } else {
    bootWhenReady();
  }
})();
